// DiningMomentCard — a small, dismissible card that proactively surfaces a
// dining suggestion when detectDiningMoments finds real free time at the
// destination, instead of requiring the traveler to think to open the chat
// assistant. Rendered inside PrimaryGpsView's children slot, only when the
// GPS hero itself is idle (no real deadline-driven next action pending) —
// see TripCardFull for that gating.

import React from 'react';
import { Utensils, X } from 'lucide-react';
import { colors, radii, typography, fontWeights } from '../../styles/designTokens';
import { formatDepartureTime } from './formatDepartureTime';

const MEAL_COPY = {
  breakfast: 'Breakfast',
  lunch: 'Lunch',
  dinner: 'Dinner',
};

const LOCATION_COPY = {
  airport: 'near the airport',
  hotel: 'near your hotel',
  'near-meeting': 'nearby',
  unknown: 'nearby',
};

const cardStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
  padding: '14px 16px',
  marginTop: '4px',
  backgroundColor: colors.infoBackground,
  border: `1px solid ${colors.divider}`,
  borderRadius: radii.inner,
};

const iconCircleStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '36px',
  height: '36px',
  borderRadius: '50%',
  backgroundColor: colors.surface,
  color: colors.infoText,
  flexShrink: 0,
};

const titleStyle = {
  fontSize: typography.body.fontSize,
  fontWeight: fontWeights.semibold,
  color: colors.textPrimary,
  margin: 0,
};

const subtitleStyle = {
  fontSize: typography.caption.fontSize,
  color: colors.textSecondary,
  margin: '2px 0 0',
};

const findButtonStyle = {
  marginTop: '10px',
  padding: '7px 14px',
  borderRadius: radii.button,
  border: 'none',
  backgroundColor: colors.info,
  color: '#ffffff',
  fontSize: typography.caption.fontSize,
  fontWeight: fontWeights.semibold,
  cursor: 'pointer',
  minHeight: '32px',
};

const dismissButtonStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '28px',
  height: '28px',
  border: 'none',
  background: 'transparent',
  color: colors.textSecondary,
  cursor: 'pointer',
  flexShrink: 0,
  borderRadius: radii.inner,
};

/**
 * @param {Object} props
 * @param {Object} props.diningMoment - `detectDiningMoments(...)` result (non-null)
 * @param {() => void} props.onOpen - Opens the DiningMomentSheet
 * @param {() => void} props.onDismiss - Dismisses this suggestion (persisted by the caller)
 */
export function DiningMomentCard({ diningMoment, onOpen, onDismiss }) {
  const mealLabel = MEAL_COPY[diningMoment.mealType] || 'A meal';
  const locationLabel = LOCATION_COPY[diningMoment.locationContext] || 'nearby';
  const untilLabel = formatDepartureTime(diningMoment.windowEnd);

  return (
    <div data-testid="dining-moment-card" style={cardStyle}>
      <div style={iconCircleStyle} aria-hidden="true">
        <Utensils size={18} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p data-testid="dining-moment-title" style={titleStyle}>
          {mealLabel} time {locationLabel}
        </p>
        <p style={subtitleStyle}>
          {untilLabel ? `Free until ${untilLabel}` : "You're free right now"} — want restaurant options?
        </p>
        <button
          type="button"
          data-testid="dining-moment-find-button"
          onClick={onOpen}
          style={findButtonStyle}
        >
          Find restaurants
        </button>
      </div>
      <button
        type="button"
        data-testid="dining-moment-dismiss-button"
        onClick={onDismiss}
        style={dismissButtonStyle}
        aria-label="Dismiss dining suggestion"
        title="Dismiss"
      >
        <X size={16} aria-hidden="true" />
      </button>
    </div>
  );
}

export default DiningMomentCard;
